import React, { useEffect, useRef } from 'react';
import { Image, Settings } from 'lucide-react';
import { useDevice } from '@/app/lib/contexts/DeviceContext';
import { useTranslations } from 'next-intl';

interface ImageOptionsPanelProps {
  isVisible: boolean;
  onClose: () => void;
  onShowMe: () => void;
  onCustomize: () => void;
  onShowMeBoobs: () => void;
  onShowMeNaked: () => void;
  imageButtonRef?: React.RefObject<HTMLButtonElement | null>;
}

export const ImageOptionsPanel: React.FC<ImageOptionsPanelProps> = ({
  isVisible,
  onClose,
  onShowMe,
  onCustomize,
  onShowMeBoobs,
  onShowMeNaked,
  imageButtonRef,
}) => {
  const t = useTranslations('chat');
  const panelRef = useRef<HTMLDivElement>(null);
  const { isMobile } = useDevice();

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!isVisible) return;

      // Don't close if clicking on the image button
      if (imageButtonRef?.current && imageButtonRef.current.contains(event.target as Node)) {
        return;
      }

      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isVisible) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isVisible, onClose, imageButtonRef]);

  // Prevent body scroll when panel is open on mobile
  useEffect(() => {
    if (isVisible && isMobile) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isVisible, isMobile]);

  if (!isVisible) return null;

  return (
    <div
      ref={panelRef}
      className="absolute bottom-full left-0 mb-1 z-50 overflow-hidden"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      {/* Dark grey overlay with blurred background - match SidePanel styling */}
      <div className="absolute inset-0 bg-[#2a2a2e] backdrop-blur-sm rounded-xl border border-white/5" />
      
      {/* Panel content */}
      <div className="relative p-2">
        <div className="space-y-0 min-w-[240px]">
          {/* Show Me Boobs Button */}
          {/* <button
            onClick={() => {
              onShowMeBoobs();
              onClose();
            }}
            className="flex items-center justify-start w-full h-10 px-1 text-white hover:bg-[#ffffff1a] transition-colors border-b border-white/5"
          >
            <div className="flex items-center gap-2">
              <span className="text-lg">🍒</span>
              <span className="text-sm font-medium font-['Montserrat',Helvetica]">
                Show me boobs
              </span>
            </div>
          </button> */}

          {/* Show Me Naked Button */}
          {/* <button
            onClick={() => {
              onShowMeNaked();
              onClose();
            }}
            className="flex items-center justify-start w-full h-10 px-1 text-white hover:bg-[#ffffff1a] transition-colors border-b border-white/5"
          >
            <div className="flex items-center gap-2">
              <span className="text-lg">👙</span>
              <span className="text-sm font-medium font-['Montserrat',Helvetica]">
                Show me naked
              </span>
            </div>
          </button> */}

          {/* Quick Generate Button */}
          <button
            onClick={() => {
              onShowMe();
              onClose();
            }}
            className="flex flex-col items-start justify-start w-full h-auto px-1 py-2 text-white hover:bg-[#ffffff1a] transition-colors rounded-t-lg"
          >
            <div className="flex items-center gap-2 mb-1">
              <Image className="w-4 h-4" />
              <span className="text-sm font-medium font-['Montserrat',Helvetica]">
                {t('quickGenerate')}
              </span>
            </div>
            {/* Helper text */}
            <div className="px-2 text-xs text-left font-light text-white/60">
              {t('quickGenerateDescription')}
            </div>
          </button>

          {/* Customize Button */}
          <button
            onClick={() => {
              onCustomize();
              onClose();
            }}
            className="flex flex-col items-start justify-start w-full h-auto px-1 py-2 text-white hover:bg-[#ffffff1a] transition-colors border-t border-white/5 rounded-b-lg"
          >
            <div className="flex items-center gap-2 mb-1">
              <Settings className="w-4 h-4" />
              <span className="text-sm font-medium font-['Montserrat',Helvetica]">
                {t('customizeCharacter')}
              </span>
            </div>
            {/* Helper text */}
            <div className="px-2 text-xs text-left font-light text-white/60">
              {t('customizeCharacterDescription')}
            </div>
          </button>
        </div>
      </div>
    </div>
  );
};
